import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getErrorKey } from '../services/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_ENABLED = GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'placeholder-for-google-oauth';

const styles = {
  container: {
    minHeight: 'calc(100vh - 180px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#ffffff',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '800',
    color: '#0d1f17',
    textAlign: 'center',
    marginBottom: '0.5rem',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#718096',
    textAlign: 'center',
    marginBottom: '2rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontWeight: '600',
    color: '#0d1f17',
    fontSize: '0.88rem',
  },
  input: {
    padding: '0.85rem 1.1rem',
    border: '1.5px solid #e2e8f0',
    borderRadius: '50px',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
  },
  inputFocus: {
    borderColor: '#FFD700',
  },
  button: {
    backgroundColor: '#FFD700',
    color: '#0d1f17',
    padding: '0.9rem',
    border: 'none',
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'background 0.15s',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    margin: '1.5rem 0',
    color: '#a0aec0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e2e8f0',
  },
  googleContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  link: {
    textAlign: 'center',
    marginTop: '1.5rem',
    color: '#718096',
    fontSize: '0.82rem',
  },
  linkAnchor: {
    color: '#0d1f17',
    textDecoration: 'none',
    fontWeight: '700',
  },
  error: {
    backgroundColor: '#fed7d7',
    color: '#c53030',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  userTypeSelect: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
  },
  userTypeButton: {
    flex: 1,
    padding: '0.75rem',
    border: '1.5px solid #e2e8f0',
    borderRadius: '50px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  userTypeActive: {
    borderColor: '#FFD700',
    backgroundColor: 'rgba(255,215,0,0.1)',
    color: '#0d1f17',
    fontWeight: '700',
  },
};

const Login = () => {
  const location = useLocation();
  const prefill = location.state?.prefill || {};
  const [email, setEmail] = useState(prefill.email || prefill.phone || '');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('client');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const { login, googleLogin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, true);
      if (result.user.userType === 'client') window.location.href = '/';
      else navigate('/attorney/dashboard');
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'loginFailed'}`));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const result = await googleLogin(credentialResponse.credential, userType);
      if (result.user.userType === 'client') window.location.href = '/';
      else navigate('/attorney/dashboard');
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'googleLoginFailed'}`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t('auth.loginTitle')}</h1>
        <p style={styles.subtitle}>{t('auth.loginSubtitle')}</p>

        {error && <div style={styles.error}>{error}</div>}

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('auth.emailOrPhone')}</label>
            <input
              type="text"
              style={{ ...styles.input, ...(focused === 'email' ? styles.inputFocus : {}) }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t('auth.password')}</label>
            <input
              type="password"
              style={{ ...styles.input, ...(focused === 'password' ? styles.inputFocus : {}) }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocused('password')}
              onBlur={() => setFocused(null)}
              required
            />
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? '...' : t('auth.loginButton')}
          </button>
        </form>

        {GOOGLE_ENABLED && (
          <>
            <div style={styles.divider}>
              <span style={styles.dividerLine}></span>
              <span>{t('auth.orContinueWith')}</span>
              <span style={styles.dividerLine}></span>
            </div>

            <p style={{textAlign: 'center', marginBottom: '1rem', color: '#718096', fontSize: '0.9rem'}}>
              {t('auth.selectUserType')}
            </p>
            <div style={styles.userTypeSelect}>
              <button
                type="button"
                style={{
                  ...styles.userTypeButton,
                  ...(userType === 'client' ? styles.userTypeActive : {})
                }}
                onClick={() => setUserType('client')}
              >
                {t('dashboard.client')}
              </button>
              <button
                type="button"
                style={{
                  ...styles.userTypeButton,
                  ...(userType === 'attorney' ? styles.userTypeActive : {})
                }}
                onClick={() => setUserType('attorney')}
              >
                {t('dashboard.attorney')}
              </button>
            </div>

            <div style={styles.googleContainer}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google login failed')}
              />
            </div>
          </>
        )}

        <p style={styles.link}>
          {t('auth.noAccount')}{' '}
          <Link to="/register" style={styles.linkAnchor}>{t('auth.signUp')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
